import React from "react";
import errorBalanceImage from "assets/error_gear_graphic.svg";
import { IBiller } from "types/biller";
import { useNavigate } from "react-router";
import {
  BALANCE_NAMESPACE,
  PAYMENT_RECEIPT_NAMESPACE,
} from "constants/i18n-namespaces";
import { Banner, Button, Tooltip, TooltipEnums } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { getLabelWithKrn, phoneFormat } from "utilities/helper";
import { getBannerRole } from "utilities/banner-accessibility";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";
import { useDispatch, useSelector } from "react-redux";
import { getIsMobile, getLabelsWithKrn, getPayableSession } from "selectors";
import { setAppLoading } from "actions/appStateActions";
import { USER_LOGOUT } from "actions/userActions";
import { setPayableSession } from "actions/payableSessionActions";
import IamApi from "api/IamApi";
import routing from "routing";
import { useCurrencyFormatter } from "hooks";

interface ErrorPayableDashboardProps {
  visible: boolean;
  biller: IBiller;
  accountNumber?: string;
  invoiceNumber?: string;
}

export const ErrorPayableDashboard = ({
  visible,
  biller,
  accountNumber,
  invoiceNumber,
}: ErrorPayableDashboardProps) => {
  const payableSession = useSelector(getPayableSession);
  const isMobile = useSelector(getIsMobile);
  const currencyFormatter = useCurrencyFormatter();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation(BALANCE_NAMESPACE);
  const labelSourceWithKrn = useSelector(getLabelsWithKrn);
  // get required values from subresources
  const contactUs: any = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  });

  const contactNumber = phoneFormat(contactUs?.properties?.phone ?? "");

  const handleLogoutClick = async () => {
    dispatch(setAppLoading(true));
    dispatch({ type: USER_LOGOUT });
    dispatch(setPayableSession(undefined));
    IamApi.ezpay.logout();
    navigate(routing.index());
  };

  const getBannerMessageLabel = () => {
    if (
      !payableSession?.payableType?.allowOverPayment &&
      payableSession?.payable?.payableAmount === 0
    ) {
      return t("no-payment-due-overpayment-not-allowed");
    } else {
      return t("could-not-load-balance-banner", { phone: contactNumber });
    }
  };

  const getBalanceContent = () => {
    if (
      !payableSession?.payableType?.allowOverPayment &&
      payableSession?.payable?.payableAmount === 0
    ) {
      return (
        <>
          <div className="ez-pay-balance-content-center-amount-due-row">
            {t("amount-due")}
            <Tooltip
              direction={isMobile ? TooltipEnums.TopLeft : TooltipEnums.Right}
              content={t("amount-due-tooltip")}
              classNames="amount-due-row-tooltip"
              data-cy="amount-due-tooltip"
              variant="rich"
            />
          </div>
          <div className="ez-pay-balance-content-center">
            <div
              className="ez-pay-balance-label-large"
              data-cy="final-amount-due"
            >
              {currencyFormatter.format(payableSession.payable.payableAmount)}
            </div>
          </div>
        </>
      );
    } else {
      return (
        <div className="ez-pay-no-balance-content-center-amount-due-column">
          <img
            data-testid="error-loading-balance"
            src={errorBalanceImage}
            alt="could-not-load-balance"
          ></img>
          <div className="could-not-load-balance">
            {t("could-not-load-balance")}
          </div>
        </div>
      );
    }
  };

  if (visible) {
    return (
      <>
        <div className="ez-pay-payable-banner" role={getBannerRole("info")}>
          <Banner
            active={true}
            variant={"info"}
            messageLabel={getBannerMessageLabel()}
            withClose={true}
            withIcon={true}
            data-cy="banner-info-content"
          />
        </div>
        <div data-testid="error-balance">
          <div className="ez-pay-error-balance-frame">
            <div className="ez-pay-balance-top">
              <PayableCardHeader />
              <div className="ez-pay-balance-row">
                {getBalanceContent()}
                <div className="ez-pay-balance-content-center-small">
                  {!accountNumber ? (
                    <div className="ez-pay-balance-content-cell">
                      {getLabelWithKrn(
                        labelSourceWithKrn.labelsWithKrn,
                        payableSession?.payable?.typeKrn ?? null,
                        "invoiceNumber",
                      )}
                      <span
                        className="ez-pay-balance-cell-value"
                        data-cy="invoice-number"
                      >
                        {invoiceNumber}
                      </span>
                    </div>
                  ) : (
                    <div className="ez-pay-balance-content-cell">
                      {getLabelWithKrn(
                        labelSourceWithKrn.labelsWithKrn,
                        payableSession?.payable?.typeKrn ?? null,
                        "accountNumber",
                      )}
                      <span
                        className="ez-pay-balance-cell-value"
                        data-cy="account-number"
                      >
                        {accountNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="ez-pay-balance-divider"></div>

              <form>
                <div className="ez-pay-payable-continue-button">
                  <Button
                    type="button"
                    className="validate-button"
                    onClick={() => handleLogoutClick()}
                    dataCy="button-validate"
                  >
                    {t("change-account", {
                      ns: PAYMENT_RECEIPT_NAMESPACE,
                      name: biller?.billerName,
                    })}
                  </Button>
                </div>
                <div className="contact-footer">
                  <ContactAndTocFooter biller={biller} />
                </div>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }
  return null;
};
